import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import {
  Platform,
  Animated as RNAnimated,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
  ViewStyle,
} from "react-native"

import type { RiveRef } from "rive-react-native"
import IonIcons from "@expo/vector-icons/Ionicons"
import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { Image, ImageStyle } from "expo-image"
import { Gesture, GestureDetector } from "react-native-gesture-handler"
import Animated, {
  interpolate,
  ReduceMotion,
  runOnJS,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withSpring,
  WithSpringConfig,
} from "react-native-reanimated"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { Alert } from "src/components/Alert/Alert"
import AvatarRive from "src/components/Rive/AvatarRive"
import { getRiveResourceName } from "src/components/Rive/riveUtils"
import { useAuth } from "src/context/AuthContext"
import { useAvatarMode } from "src/context/AvatarModeContext"
import { useChat } from "src/context/ChatContext"
import { useRiveContext } from "src/context/RiveContext"
import { useSidekickInteraction } from "src/context/SidekickInteractionContext"
import { useVisemes } from "src/context/VisemeContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { MAX_CHARS_PER_LINE } from "src/hooks/useSpeechMarkPlayer"
import { useStatusBarCompensation } from "src/hooks/useStatusBarCompensation"
import { useWebAvatarDrag } from "src/hooks/useWebAvatarDrag"
import { useWebLayoutConstraints, WEB_CANVAS_MAX_WIDTH } from "src/hooks/useWebLayoutConstraints"
import { ThemedStyle } from "src/theme"
import { endAvatarGesture, startAvatarGesture } from "src/utils/avatarGestureGuard"
import type { Rive as CanvasRive } from "@rive-app/canvas"

/**
 * Set this to `true` to print verbose `[avatar-rngh]` traces for the RNGH
 * gesture lifecycle. Pair with the matching flags in `useWebAvatarDrag.ts`
 * and `avatarGestureGuard.ts` to diagnose drag/tap regressions on web.
 *
 * Note: on web these gestures are intentionally disabled (see comments on
 * `panGesture` / `tapGesture` below), so seeing any of these logs in the
 * browser console means RNGH is still firing on web — which would itself be
 * useful diagnostic information.
 */
const DEBUG_AVATAR_GESTURE = false

function rlog(...args: unknown[]) {
  if (!DEBUG_AVATAR_GESTURE) return
  // eslint-disable-next-line no-console
  console.log("[avatar-rngh]", ...args)
}

// import { LightenDarkenColor } from "src/utils/colorUtils"

/**
 * Global stylesheet rule that forces `touch-action: none` on any element
 * tagged with `data-touch-action="none"`.
 *
 * Why we do this instead of just setting `touchAction: "none"` in the React
 * Native style:
 *   RN-Web + Reanimated's `Animated.View` style pipeline does NOT reliably
 *   forward `touchAction` to the DOM. Diagnostics on the avatar wrapper
 *   showed the computed `touch-action` as `auto`, despite our style array
 *   containing `touchAction: "none"`. With `touch-action: auto` on the
 *   touched element, the browser immediately commits any touch on the
 *   minimized avatar to a native scroll/zoom gesture and fires
 *   `pointercancel` before our React handler has a chance to drag — which
 *   manifested as the avatar "snapping back" to its anchor on touch.
 *
 * The bulletproof workaround: own a global CSS rule (with `!important` so
 * nothing in the cascade can override it) and tag the avatar wrapper with a
 * matching `data-touch-action="none"` attribute via React Native's
 * `dataSet` prop, which RN-Web translates directly to `data-*` HTML
 * attributes — bypassing the style pipeline entirely.
 *
 * Web-only by intent. Native platforms route gestures through RNGH.
 */
if (Platform.OS === "web" && typeof document !== "undefined") {
  const styleId = "sidekick-avatar-touch-action-none"
  if (!document.getElementById(styleId)) {
    const styleEl = document.createElement("style")
    styleEl.id = styleId
    // Match BOTH the marked element AND every descendant. RN-Web/Reanimated
    // sometimes places `dataSet` on a different node than the actual touch
    // target (the React Native style pipeline can wrap children in
    // intermediate DOM elements). Tagging an ancestor and matching all
    // descendants guarantees `touch-action: none` reaches the touched
    // element regardless of which level RN-Web ended up tagging.
    styleEl.textContent = `[data-touch-action="none"], [data-touch-action="none"] * { touch-action: none !important; }`
    document.head.appendChild(styleEl)
  }
}

const AnimatedText = Animated.createAnimatedComponent(Text)

/**
 * Avatar display modes
 */
export type AvatarMode = "large" | "minimized" | "tab"

/**
 * Props for the AvatarOverlay component
 */
export interface AvatarOverlayProps {
  /** Whether to show closed captions */
  showCaptions?: boolean
  /** Callback when CC toggle is pressed */
  onToggleCaptions?: () => void
  /** Optional owner identifier for Rive animation registration */
  riveOwner?: "primary" | "video"
  /** Callback when mode changes */
  onModeChange?: (mode: AvatarMode) => void
  /** Bottom offset for large mode - space to leave for button cluster (deprecated, use useFlexLayout instead) */
  bottomOffset?: number
  /** Use flex layout for large mode instead of absolute positioning */
  useFlexLayout?: boolean
  /** Is device in landscape orientation (for aspect ratio preservation) */
  isLandscape?: boolean
  /** Disable closed captions for mini/tab avatar views (overrides context, but not for large avatar) */
  disableCCforView?: boolean
  /** Keyboard height for adjusting avatar position when typing */
  keyboardHeight?: number
  /**
   * When true, hides conversation-specific controls (Switch-to-PSK overlay).
   * Use in unauthenticated contexts where no conversation is active.
   */
  hideConversationControls?: boolean
}

// Spring configuration for smooth mode transitions
const modeTransitionSpring: WithSpringConfig = {
  mass: 1,
  damping: 22,
  stiffness: 120,
  overshootClamping: false,
  restDisplacementThreshold: 0.01,
  restSpeedThreshold: 2,
  reduceMotion: ReduceMotion.System,
}

// Sizes for different modes
const MINIMIZED_HEIGHT = 200
const MINIMIZED_AVATAR_SCALE = 1.0
const LARGE_AVATAR_SCALE = 1.0 // Unified scale for both orientations
const LARGE_AVATAR_BOTTOM_OFFSET = 0 // Unified offset for both orientations
const TAB_SIZE = 36
const TAB_WIDTH = 24
const LARGE_MARGIN = 24
// Reference phone portrait width used to compute proportional margins on web
const PHONE_PORTRAIT_REF_WIDTH = 390
const MINIMIZED_MARGIN = 16
const LARGE_AVATAR_ASPECT_RATIO = 1.212 // width:height ratio (used for both large and minimized avatars)
const MINIMIZED_PORTRAIT_ASPECT_RATIO = 0.6
const MINIMIZED_LANDSCAPE_WIDTH = 120
const WEB_PRIMARY_HEADER_CLEARANCE = 56

/**
 * AvatarOverlay - Renders the animated avatar with three display modes:
 * - Large: Almost full-screen with minimize and mute buttons
 * - Minimized: Small draggable PiP that can be swiped away
 * - Tab: Small icon tab on the right edge that can be tapped to expand
 *
 * Captions are displayed at the bottom of the screen for all modes.
 */
export function AvatarOverlay({
  showCaptions = true,
  onToggleCaptions,
  riveOwner = "video",
  onModeChange,
  bottomOffset = 80,
  useFlexLayout = false,
  isLandscape: propIsLandscape,
  disableCCforView = false,
  keyboardHeight = 0,
  hideConversationControls = false,
}: AvatarOverlayProps) {
  const { width: screenWidth, height: screenHeight } = useWindowDimensions()
  const insets = useSafeAreaInsets()
  const { effectiveTopInset } = useStatusBarCompensation()

  // On web, the UI is constrained to a centered iPad-style container
  // so we treat it as portrait regardless of browser window dimensions
  const isLandscape =
    Platform.OS === "web" ? false : (propIsLandscape ?? screenWidth > screenHeight)

  // Web-specific layout constraints (always call hook, use result conditionally)
  const webLayout = useWebLayoutConstraints()

  // Use context for persistent mode, snap corner, mute, and captions state
  const {
    mode,
    setMode,
    snapCorner,
    setSnapCorner,
    isMuted,
    setIsMuted,
    captionsEnabled,
    setCaptionsEnabled,
  } = useAvatarMode()
  const { setMuted: setVisemeMuted, isAudioBlocked, stopSpeech } = useVisemes()
  const {
    themed,
    theme: { colors },
  } = useAppTheme() // Access theme and themed function

  // Sync mute state with VisemeContext
  useEffect(() => {
    setVisemeMuted(isMuted)
  }, [isMuted, setVisemeMuted])

  const setMuted = useCallback(
    (muted: boolean) => {
      setIsMuted(muted)
      setVisemeMuted(muted)
    },
    [setIsMuted, setVisemeMuted],
  )

  const handleToggleCaptions = useCallback(() => {
    const newValue = !captionsEnabled
    setCaptionsEnabled(newValue)
    onToggleCaptions?.()
  }, [captionsEnabled, setCaptionsEnabled, onToggleCaptions])

  // Animated values - initialize based on current mode from context
  const getModeProgress = (m: AvatarMode) => (m === "large" ? 1 : m === "minimized" ? 0.5 : 0)
  const modeProgress = useSharedValue(getModeProgress(mode))

  // For pan gesture - temporary drag offset, reset to 0 when released
  const dragOffsetX = useSharedValue(0)
  const dragOffsetY = useSharedValue(0)
  const lastDragOffsetX = useSharedValue(0)
  const lastDragOffsetY = useSharedValue(0)

  // Current corner as shared value for synchronous updates in worklet
  // 0 = top-left, 1 = top-right, 2 = bottom-left, 3 = bottom-right
  const cornerIndex = useSharedValue(
    snapCorner === "top-left"
      ? 0
      : snapCorner === "top-right"
        ? 1
        : snapCorner === "bottom-left"
          ? 2
          : 3,
  )

  // Track if this is the initial mount to avoid overriding animations
  const isInitialMount = useRef(true)
  const lastSyncedMode = useRef(mode)

  // When in large flex layout, the avatar's actual width can differ from precomputed largeWidth
  // (e.g. after onLayout). Use measured width for caption box so text isn't cut off.
  const [effectiveLargeAvatarWidth, setEffectiveLargeAvatarWidth] = useState<number | null>(null)
  const lastContainerTargetWidthRef = useRef<number | null>(null)
  // Diagnostic ref: tracks last seen `mode` so we can log transitions in
  // render. Useful for spotting unexpected mid-drag mode flips on web.
  const lastModeRef = useRef<typeof mode>(mode)
  // Shared value so worklet can cap caption font size by actual container width during rotation
  const containerTargetWidthShared = useSharedValue(Number.POSITIVE_INFINITY)
  useEffect(() => {
    if (mode !== "large") {
      setEffectiveLargeAvatarWidth(null)
      lastContainerTargetWidthRef.current = null
      containerTargetWidthShared.value = Number.POSITIVE_INFINITY
    }
  }, [mode, containerTargetWidthShared])

  // Sync cornerIndex when snapCorner changes from context
  useEffect(() => {
    const newIndex =
      snapCorner === "top-left"
        ? 0
        : snapCorner === "top-right"
          ? 1
          : snapCorner === "bottom-left"
            ? 2
            : 3
    cornerIndex.value = newIndex
  }, [snapCorner, cornerIndex])

  // Sync modeProgress when mode changes from external source (e.g., when navigating back to a screen)
  // Skip during normal transitions triggered by this component
  useEffect(() => {
    if (isInitialMount.current) {
      // On initial mount, set the value immediately (no animation needed)
      modeProgress.value = getModeProgress(mode)
      isInitialMount.current = false
      lastSyncedMode.current = mode
    } else if (mode !== lastSyncedMode.current) {
      // Mode changed from external source (e.g., context update from another screen)
      // Animate to the new mode
      lastSyncedMode.current = mode
      const targetProgress = getModeProgress(mode)
      modeProgress.value = withSpring(targetProgress, modeTransitionSpring)

      // Reset drag offset when changing modes from external source
      if (mode !== "minimized") {
        dragOffsetX.value = withSpring(0, modeTransitionSpring)
        dragOffsetY.value = withSpring(0, modeTransitionSpring)
      }
    }
  }, [mode, modeProgress, dragOffsetX, dragOffsetY])

  // Get consolidated interaction state
  const { shouldShowAvatar } = useSidekickInteraction()

  // Avatar visibility logic - now always visible since shouldShowAvatar is always true
  // The avatar mode (large, minimized, tab) controls visibility instead of opacity
  const avatarOpacity = useRef(new RNAnimated.Value(1)).current

  // Set opacity to 1 immediately since avatar never fades out
  useEffect(() => {
    avatarOpacity.setValue(1)
  }, [avatarOpacity])

  // Calculate sizes for large mode with bidirectional aspect ratio constraint
  // Portrait width is the constant basis for all width calculations
  const portraitWidth = Math.min(screenWidth, screenHeight)
  // On web, constrain to the canvas width to fit within the iPad-style centered layout
  const webConstrainedWidth = webLayout.constrainedWidth
  // Scale the margin proportionally to the web column so the avatar occupies
  // the same fraction of its container as it does on a phone screen.
  const webLargeMargin = Math.round((LARGE_MARGIN * webConstrainedWidth) / PHONE_PORTRAIT_REF_WIDTH)
  const availableWidth =
    Platform.OS === "web"
      ? Math.min(portraitWidth - LARGE_MARGIN * 2, webConstrainedWidth - webLargeMargin * 2)
      : portraitWidth - LARGE_MARGIN * 2

  // Use stable inset values for target calculations to avoid "dips" during rotation
  // when screen dimensions update before safe area insets.
  const stableTop = isLandscape ? Math.min(effectiveTopInset, 20) : effectiveTopInset
  const stableBottom = isLandscape ? Math.min(insets.bottom, 21) : insets.bottom
  // For flex layout, we want to center the avatar in the entire region between top of screen and controls
  // without subtracting safe area insets from the centering logic itself. This matches the behavior
  // of the flex layout in SidekickView.
  const flexRegionHeight = screenHeight - bottomOffset
  // Available height must account for top inset to avoid extending into status bar/notch area
  const availableHeight = flexRegionHeight - effectiveTopInset

  // Calculate dimensions maintaining 1.22:1 aspect ratio, constrained by available space
  // Test both scenarios to find which dimension is the limiting factor
  const widthBasedHeight = availableWidth / LARGE_AVATAR_ASPECT_RATIO
  const heightBasedWidth = availableHeight * LARGE_AVATAR_ASPECT_RATIO

  let largeWidth: number
  let largeHeight: number

  if (widthBasedHeight <= availableHeight) {
    // Width is the limiting factor (typical in portrait)
    largeWidth = availableWidth
    largeHeight = widthBasedHeight
  } else {
    // Height is the limiting factor (typical in landscape or with tall controls)
    largeWidth = heightBasedWidth
    largeHeight = availableHeight
  }

  // On wide-screen web, the avatar feels oversized relative to the layout.
  // Start shrinking at 75% of the canvas max-width so there's no sudden size
  // jump when the canvas constraint activates. Has no effect on mobile-sized screens.
  if (Platform.OS === "web" && screenWidth > 0.75 * WEB_CANVAS_MAX_WIDTH) {
    largeWidth = 0.7 * WEB_CANVAS_MAX_WIDTH
    largeHeight = largeWidth / LARGE_AVATAR_ASPECT_RATIO
  }

  // Center horizontally - use the flex-like centering to avoid "pops"
  const largeModeLeft = (screenWidth - largeWidth) / 2
  // Center vertically when height is constrained - use the flex-like centering to avoid "pops"
  // when transitioning from flex to absolute positioning.
  const largeModeTop = (flexRegionHeight - largeHeight) / 2

  // Calculate minimized dimensions (static, for fallback sync)
  const minimizedHeight = isLandscape
    ? Math.round(MINIMIZED_LANDSCAPE_WIDTH / LARGE_AVATAR_ASPECT_RATIO)
    : MINIMIZED_HEIGHT
  const minimizedWidth = isLandscape
    ? MINIMIZED_LANDSCAPE_WIDTH
    : Math.round(MINIMIZED_HEIGHT * MINIMIZED_PORTRAIT_ASPECT_RATIO)

  // Animated values for smooth rotation transitions
  const animatedLargeWidth = useSharedValue(largeWidth)
  const animatedLargeHeight = useSharedValue(largeHeight)
  const animatedLargeModeLeft = useSharedValue(largeModeLeft)
  const animatedLargeModeTop = useSharedValue(largeModeTop)

  // Animated insets for smooth transition of the container's position/padding
  const animatedTopInset = useSharedValue(stableTop)
  const animatedBottomInset = useSharedValue(stableBottom)

  // Animated values for avatar zoom/translation during rotation
  const targetLargeScale = LARGE_AVATAR_SCALE
  const targetLargeBottomOffset = LARGE_AVATAR_BOTTOM_OFFSET

  const animatedLargeAvatarScale = useSharedValue(targetLargeScale)
  const animatedLargeBottomOffset = useSharedValue(targetLargeBottomOffset)

  // Animated values for minimized mode dimensions (change with orientation)
  const animatedMinimizedWidth = useSharedValue(minimizedWidth)
  const animatedMinimizedHeight = useSharedValue(minimizedHeight)

  // Keyboard offset for adjusting avatar position when typing
  const keyboardOffset = useSharedValue(0)

  // Shared values for minimized mode snap points (reactive to keyboard)
  // Initialize with calculated values
  const initialContentHeight = screenHeight - effectiveTopInset - insets.bottom
  const initialSnapPointsYMin = isLandscape ? 0 : 60
  const initialAndroidBottomAdjustment = Platform.OS === "android" ? 40 : 0
  const initialSnapPointsYMax =
    initialContentHeight -
    minimizedHeight -
    MINIMIZED_MARGIN -
    120 -
    initialAndroidBottomAdjustment -
    (keyboardHeight > 0 ? keyboardHeight : 0)
  const snapPointsYMaxShared = useSharedValue(initialSnapPointsYMax)
  const snapPointsYMinShared = useSharedValue(initialSnapPointsYMin)

  // Helper to calculate dimensions and avatar scale for a given orientation

  // Spring config for rotation animation - matches typical 300ms rotation
  const rotationSpringConfig = useMemo(
    () => ({
      mass: 0.8,
      damping: 18,
      stiffness: 150,
      overshootClamping: true,
    }),
    [],
  )

  // Calculate current avatar scale values for synchronization
  const largeAvatarScale = LARGE_AVATAR_SCALE
  const largeBottomOffset = LARGE_AVATAR_BOTTOM_OFFSET

  // Track what orientation we're currently animating toward
  const targetOrientation = useRef<boolean | null>(null)
  const hasInitializedDimensions = useRef(false)

  // Sync dimensions when orientation or screen size changes
  useLayoutEffect(() => {
    // Check if we need to animate to a new orientation
    const needsAnimation = targetOrientation.current !== isLandscape

    if (!hasInitializedDimensions.current) {
      // Initial mount - set values directly without animation
      animatedLargeWidth.value = largeWidth
      animatedLargeHeight.value = largeHeight
      animatedLargeModeLeft.value = largeModeLeft
      animatedLargeModeTop.value = largeModeTop
      animatedLargeAvatarScale.value = largeAvatarScale
      animatedLargeBottomOffset.value = largeBottomOffset
      animatedMinimizedWidth.value = minimizedWidth
      animatedMinimizedHeight.value = minimizedHeight
      animatedTopInset.value = stableTop
      animatedBottomInset.value = stableBottom
      hasInitializedDimensions.current = true
      targetOrientation.current = isLandscape
    } else if (needsAnimation) {
      // Update target orientation FIRST to prevent duplicate animations
      targetOrientation.current = isLandscape

      // Orientation changed - animate to new dimensions
      animatedLargeWidth.value = withSpring(largeWidth, rotationSpringConfig)
      animatedLargeHeight.value = withSpring(largeHeight, rotationSpringConfig)
      animatedLargeModeLeft.value = withSpring(largeModeLeft, rotationSpringConfig)
      animatedLargeModeTop.value = withSpring(largeModeTop, rotationSpringConfig)
      animatedLargeAvatarScale.value = withSpring(largeAvatarScale, rotationSpringConfig)
      animatedLargeBottomOffset.value = withSpring(largeBottomOffset, rotationSpringConfig)
      animatedMinimizedWidth.value = withSpring(minimizedWidth, rotationSpringConfig)
      animatedMinimizedHeight.value = withSpring(minimizedHeight, rotationSpringConfig)
      animatedTopInset.value = withSpring(stableTop, rotationSpringConfig)
      animatedBottomInset.value = withSpring(stableBottom, rotationSpringConfig)
    }
  }, [
    isLandscape,
    largeWidth,
    largeHeight,
    largeModeLeft,
    largeModeTop,
    largeAvatarScale,
    largeBottomOffset,
    minimizedWidth,
    minimizedHeight,
    stableTop,
    stableBottom,
    rotationSpringConfig,
    animatedLargeWidth,
    animatedLargeHeight,
    animatedLargeModeLeft,
    animatedLargeModeTop,
    animatedLargeAvatarScale,
    animatedLargeBottomOffset,
    animatedMinimizedWidth,
    animatedMinimizedHeight,
    animatedTopInset,
    animatedBottomInset,
    screenWidth,
    screenHeight,
    effectiveTopInset,
    insets.bottom,
    bottomOffset,
  ])

  // Animate keyboard offset when keyboard appears/disappears
  useEffect(() => {
    if (keyboardHeight > 0 && mode === "large") {
      // Move avatar up by half the keyboard height to keep it centered in available space
      keyboardOffset.value = withSpring(-keyboardHeight / 2, {
        damping: 20,
        stiffness: 200,
      })
    } else {
      keyboardOffset.value = withSpring(0, {
        damping: 20,
        stiffness: 200,
      })
    }
  }, [keyboardHeight, mode, keyboardOffset])

  // Update snap points when keyboard height or screen dimensions change
  useEffect(() => {
    const contentHeight = screenHeight - effectiveTopInset - insets.bottom
    const yMin = isLandscape ? 0 : 60
    const androidBottomAdjustment = Platform.OS === "android" ? 40 : 0
    const keyboardAdjustment = keyboardHeight > 0 ? keyboardHeight : 0
    const yMax =
      contentHeight -
      minimizedHeight -
      MINIMIZED_MARGIN -
      120 -
      androidBottomAdjustment -
      keyboardAdjustment

    // Animate the snap points for smooth transitions
    if (mode === "minimized") {
      snapPointsYMaxShared.value = withSpring(yMax, {
        damping: 20,
        stiffness: 200,
      })
      snapPointsYMinShared.value = yMin
    } else {
      // Update immediately if not in minimized mode
      snapPointsYMaxShared.value = yMax
      snapPointsYMinShared.value = yMin
    }
  }, [
    keyboardHeight,
    screenHeight,
    effectiveTopInset,
    insets.bottom,
    isLandscape,
    minimizedHeight,
    mode,
    snapPointsYMaxShared,
    snapPointsYMinShared,
  ])

  const charWidthRatio = 0.5 // average character width ratio (smaller = larger font)

  // Rive context
  const {
    riveMskAvatarId,
    resolvedAvatarId,
    registerAnimationFunctions,
    captionText = { line1: "", line2: "" },
    isCaptionActive = false,
    avatarName,
    mskImageSource,
    isMsk,
  } = useRiveContext()

  const { api } = useAuth()
  const { startNewConversation } = useChat()

  const handleSwitchToPsk = useCallback(() => {
    Alert.alert("Switch to Personal Sidekick", "Switch back to your Personal Sidekick?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Confirm",
        style: "default",
        onPress: async () => {
          await stopSpeech()
          try {
            await api.closeCurrentConversation()
          } catch (error) {
            console.error("[AvatarOverlay] Failed to close current conversation:", error)
          }
          startNewConversation()
        },
      },
    ])
  }, [api, startNewConversation, stopSpeech])

  // resolvedAvatarId is computed synchronously from avatarUserForConversation (accounts for
  // both MSK and ESK conversations) and always reflects the latest query data. Use it over
  // riveMskAvatarId (which lags by one render due to its async effect) to prevent a flash of
  // the wrong avatar when navigating to this screen after an MSK change.
  const effectiveAvatarId = resolvedAvatarId !== -1 ? resolvedAvatarId : (riveMskAvatarId ?? -1)
  const riveResourceName = getRiveResourceName(effectiveAvatarId)
  const [riveAvatarRef, setRiveAvatarRef] = useState<RiveRef | CanvasRive | null>(null)

  // Calculate gradient colors for avatar background - active avatar uses avatarBackground
  const baseColor = colors.avatarBackground
  /* Temporarily commented out gradient colors
  const avatarGradientColors = useMemo(
    () => [LightenDarkenColor(baseColor, 40), baseColor, LightenDarkenColor(baseColor, -40)],
    [baseColor],
  )
  */

  // Register Rive animation functions when the ref is available
  useEffect(() => {
    if (!registerAnimationFunctions) {
      return undefined
    }

    registerAnimationFunctions(riveOwner, riveAvatarRef)
    return () => {
      registerAnimationFunctions(riveOwner, null)
    }
  }, [riveAvatarRef, registerAnimationFunctions, riveOwner])

  const shouldShowCaptions = useMemo(() => {
    return (
      showCaptions &&
      captionsEnabled &&
      isCaptionActive &&
      (!!captionText.line1 || !!captionText.line2)
    )
  }, [showCaptions, captionsEnabled, isCaptionActive, captionText.line1, captionText.line2])

  // Determine if captions should be shown based on mode and backdrop settings
  const shouldShowCaptionsForMode = useMemo(() => {
    if (mode === "large") {
      // Large mode: ignore disableCCforView, always render inside container (still respects captionsEnabled toggle)
      return true
    } else {
      // Minimized/tab modes: show captions only if not disabled
      return !disableCCforView
    }
  }, [mode, disableCCforView])

  // Mode transition handlers
  const transitionToMode = useCallback(
    (newMode: AvatarMode, isManual = false) => {
      const previousMode = mode

      // Update the ref first so the useEffect knows this is an internal transition
      lastSyncedMode.current = newMode

      setMode(newMode, isManual)
      onModeChange?.(newMode)

      // When expanding to large mode, ensure positioning values are synchronized immediately
      // to avoid using stale/animating values from orientation changes
      if (newMode === "large" && previousMode !== "large") {
        animatedLargeModeLeft.value = largeModeLeft
        animatedLargeModeTop.value = largeModeTop
        animatedLargeWidth.value = largeWidth
        animatedLargeHeight.value = largeHeight
        animatedLargeAvatarScale.value = targetLargeScale
        animatedLargeBottomOffset.value = targetLargeBottomOffset
      }

      const targetProgress = newMode === "large" ? 1 : newMode === "minimized" ? 0.5 : 0
      modeProgress.value = withSpring(targetProgress, modeTransitionSpring)

      // Reset drag offset when changing modes
      if (newMode !== "minimized") {
        dragOffsetX.value = withSpring(0, modeTransitionSpring)
        dragOffsetY.value = withSpring(0, modeTransitionSpring)
      }
    },
    [
      modeProgress,
      dragOffsetX,
      dragOffsetY,
      onModeChange,
      setMode,
      mode,
      animatedLargeModeLeft,
      largeModeLeft,
      animatedLargeModeTop,
      largeModeTop,
      animatedLargeWidth,
      largeWidth,
      animatedLargeHeight,
      largeHeight,
      animatedLargeAvatarScale,
      targetLargeScale,
      animatedLargeBottomOffset,
      targetLargeBottomOffset,
    ],
  )

  const handleMinimize = useCallback(() => {
    transitionToMode("minimized", true) // Manual transition
  }, [transitionToMode])

  const handleExpand = useCallback(() => {
    transitionToMode("large", true) // Manual transition
  }, [transitionToMode])

  const handleDismissToTab = useCallback(() => {
    transitionToMode("tab", true) // Manual transition
  }, [transitionToMode])

  const handleTabTap = useCallback(() => {
    transitionToMode("minimized", true) // Manual transition - expanding from tab
  }, [transitionToMode])

  // When Safari's autoplay policy blocks audio, force-mute so audio plays silently
  // until the user explicitly taps the mute button to unmute. The document-level
  // touchstart listener handles the silent AudioContext unlock independently.
  useEffect(() => {
    if (isAudioBlocked) {
      setMuted(true)
    }
  }, [isAudioBlocked, setMuted])

  const handleToggleMute = useCallback(() => {
    setMuted(!isMuted)
  }, [isMuted, setMuted])

  // Web-specific drag handling for minimized mode (always call hook, use result conditionally)
  const webDrag = useWebAvatarDrag({
    snapCorner,
    setSnapCorner,
    minimizedWidth,
    minimizedHeight,
    // Only the primary/chat surface has the extra top brand/header stack on web (riveOwner === "primary").
    topClearance:
      Platform.OS === "web" && riveOwner === "primary" ? WEB_PRIMARY_HEADER_CLEARANCE : 0,
    isEnabled: Platform.OS === "web" && mode === "minimized",
    onExpand: handleExpand,
    onDismissToTab: handleDismissToTab,
  })

  // Initialize web position when entering minimized mode
  useEffect(() => {
    if (Platform.OS === "web" && mode === "minimized") {
      webDrag.initializePosition()
    }
    // Only run when mode changes, not when webDrag changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode])

  // Calculate snap points for minimized mode based on screen dimensions
  const contentWidth = screenWidth - insets.left - insets.right
  const contentHeight = screenHeight - effectiveTopInset - insets.bottom

  const snapPointsXMin = isLandscape ? 70 : MINIMIZED_MARGIN // Left edge (more space in landscape for controls)
  const androidRightAdjustment = Platform.OS === "android" && isLandscape ? 80 : 0 // Android needs extra space on right in landscape to prevent off-screen positioning
  const snapPointsXMax =
    contentWidth - minimizedWidth - MINIMIZED_MARGIN - 16 - androidRightAdjustment // Right edge (extra buffer)
  // Y snap points are now managed by shared values (snapPointsYMinShared, snapPointsYMaxShared)
  // which are updated in the useEffect above to account for keyboard height

  // Pan gesture for minimized mode.
  // IMPORTANT: disabled on web. On web, drag/tap/dismiss for minimized mode
  // are driven by `useWebAvatarDrag` (Pointer Events updating `webPosition`).
  // Leaving RNGH enabled on web caused it to compete for the same pointer
  // events and, after a maximize→minimize cycle, RNGH started winning the
  // race and consuming events before our React pointer handlers could fire —
  // breaking both drag (no `webPosition` update) and tap-to-expand event
  // ordering (synthetic click bleeding through to the underlying Pressable).
  const panGesture = Gesture.Pan()
    .enabled(Platform.OS !== "web" && mode === "minimized")
    .onBegin(() => {
      "worklet"
      lastDragOffsetX.value = dragOffsetX.value
      lastDragOffsetY.value = dragOffsetY.value
      // Mark the avatar gesture active on native so any sibling tap-capture
      // handlers can bail out. On web this gesture is disabled and the guard
      // is armed by `useWebAvatarDrag`'s pointer handlers instead.
      runOnJS(startAvatarGesture)()
      runOnJS(rlog)("pan.onBegin")
    })
    .onUpdate((event) => {
      "worklet"
      dragOffsetX.value = lastDragOffsetX.value + event.translationX
      dragOffsetY.value = lastDragOffsetY.value + event.translationY
    })
    .onEnd((event) => {
      "worklet"
      // Get current corner position from cornerIndex
      const isCurrentLeft = cornerIndex.value === 0 || cornerIndex.value === 2
      const isCurrentTop = cornerIndex.value === 0 || cornerIndex.value === 1
      const currentX = isCurrentLeft ? snapPointsXMin : snapPointsXMax
      const currentY = isCurrentTop ? snapPointsYMinShared.value : snapPointsYMaxShared.value

      // Calculate projected position with velocity
      const projectedX = currentX + dragOffsetX.value + 0.05 * event.velocityX
      const projectedY = currentY + dragOffsetY.value + 0.05 * event.velocityY

      // Check if thrown off screen
      const isThrownOffLeft = projectedX < -minimizedWidth * 0.75
      const isThrownOffRight = projectedX > contentWidth + minimizedWidth * 0.25
      const isThrownOffTop = projectedY < -minimizedHeight * 0.75
      const isThrownOffBottom = projectedY > contentHeight + minimizedHeight * 0.25

      if (isThrownOffLeft || isThrownOffRight || isThrownOffTop || isThrownOffBottom) {
        runOnJS(handleDismissToTab)()
        // Reset drag offset
        dragOffsetX.value = withSpring(0, modeTransitionSpring)
        dragOffsetY.value = withSpring(0, modeTransitionSpring)
        return
      }

      // Determine nearest corner
      const distToLeft = Math.abs(projectedX - snapPointsXMin)
      const distToRight = Math.abs(projectedX - snapPointsXMax)
      const isLeft = distToLeft < distToRight

      const distToTop = Math.abs(projectedY - snapPointsYMinShared.value)
      const distToBottom = Math.abs(projectedY - snapPointsYMaxShared.value)
      const isTop = distToTop < distToBottom

      // Calculate new corner index (0=TL, 1=TR, 2=BL, 3=BR)
      const newCornerIndex = isTop ? (isLeft ? 0 : 1) : isLeft ? 2 : 3

      // Calculate new corner position based on redirected corner
      const finalIsLeft = newCornerIndex === 0 || newCornerIndex === 2
      const finalIsTop = newCornerIndex === 0 || newCornerIndex === 1
      const newCornerX = finalIsLeft ? snapPointsXMin : snapPointsXMax
      const newCornerY = finalIsTop ? snapPointsYMinShared.value : snapPointsYMaxShared.value

      // Calculate current absolute position
      const absoluteX = currentX + dragOffsetX.value
      const absoluteY = currentY + dragOffsetY.value

      // Calculate offset relative to new corner (maintains visual position during transition)
      const offsetFromNewCornerX = absoluteX - newCornerX
      const offsetFromNewCornerY = absoluteY - newCornerY

      // Update corner index immediately (synchronous, no re-render)
      cornerIndex.value = newCornerIndex

      // Set dragOffset to maintain visual position, then animate to 0
      dragOffsetX.value = offsetFromNewCornerX
      dragOffsetY.value = offsetFromNewCornerY

      dragOffsetX.value = withSpring(
        0,
        {
          velocity: event.velocityX,
          mass: 1,
          damping: 20,
          stiffness: 280,
        },
        (finished) => {
          "worklet"
          if (finished) {
            // Sync to context after animation completes
            const cornerName =
              newCornerIndex === 0
                ? "top-left"
                : newCornerIndex === 1
                  ? "top-right"
                  : newCornerIndex === 2
                    ? "bottom-left"
                    : "bottom-right"
            runOnJS(setSnapCorner)(cornerName)
          }
        },
      )
      dragOffsetY.value = withSpring(0, {
        velocity: event.velocityY,
        mass: 1,
        damping: 20,
        stiffness: 280,
      })
    })
    .onFinalize(() => {
      "worklet"
      // Always clear the gesture guard when the gesture ends, regardless of
      // whether it was a pan, tap, or cancellation. Pairs with the
      // `startAvatarGesture` call in `onBegin`.
      runOnJS(endAvatarGesture)()
      runOnJS(rlog)("pan.onFinalize")
    })

  // Tap gesture for minimized mode (expand to large).
  // Disabled on web for the same reason as panGesture above — see comment.
  const tapGesture = Gesture.Tap()
    .enabled(Platform.OS !== "web" && mode === "minimized")
    .onBegin(() => {
      "worklet"
      runOnJS(startAvatarGesture)()
      runOnJS(rlog)("tap.onBegin")
    })
    .onEnd(() => {
      "worklet"
      runOnJS(handleExpand)()
      runOnJS(rlog)("tap.onEnd")
    })
    .onFinalize(() => {
      "worklet"
      runOnJS(endAvatarGesture)()
      runOnJS(rlog)("tap.onFinalize")
    })

  // Pan gesture for large mode (swipe to tab)
  const largePanGesture = Gesture.Pan()
    .enabled(mode === "large")
    .onBegin(() => {
      "worklet"
      lastDragOffsetX.value = dragOffsetX.value
      lastDragOffsetY.value = dragOffsetY.value
    })
    .onUpdate((event) => {
      "worklet"
      dragOffsetX.value = lastDragOffsetX.value + event.translationX
      dragOffsetY.value = lastDragOffsetY.value + event.translationY
    })
    .onEnd((event) => {
      "worklet"
      const swipeThreshold = screenWidth * 0.25
      const velocityThreshold = 500

      if (
        Math.abs(event.translationX) > swipeThreshold ||
        Math.abs(event.velocityX) > velocityThreshold
      ) {
        runOnJS(handleDismissToTab)()
      } else {
        dragOffsetX.value = withSpring(0, modeTransitionSpring)
        dragOffsetY.value = withSpring(0, modeTransitionSpring)
      }
    })

  // Combine gestures - Exclusive means pan takes priority over tap
  const combinedGesture = Gesture.Exclusive(panGesture, tapGesture, largePanGesture)

  // Base style for large mode - orientation-specific properties set outside worklet
  // This avoids the issue of stale isLandscape values in worklets during rotation
  const largeModeBaseStyle = useMemo(
    () => ({
      // Width is set explicitly via largeWidth, height fills available space
    }),
    [],
  )

  // Animated styles for the avatar container
  const avatarAnimatedStyle = useAnimatedStyle(() => {
    // Common animations
    const borderRadius = interpolate(modeProgress.value, [0, 0.5, 1], [TAB_SIZE / 2, 10, 20])
    const opacity = interpolate(modeProgress.value, [0, 0.25, 0.5, 1], [0, 0, 1, 1])

    // Calculate minimized position based on cornerIndex
    // 0 = top-left, 1 = top-right, 2 = bottom-left, 3 = bottom-right
    const isLeft = cornerIndex.value === 0 || cornerIndex.value === 2
    const isTop = cornerIndex.value === 0 || cornerIndex.value === 1
    const minimizedX = isLeft ? snapPointsXMin : snapPointsXMax
    const minimizedY = isTop ? snapPointsYMinShared.value : snapPointsYMaxShared.value

    if (useFlexLayout) {
      // Large mode with flex layout
      return {
        position: "relative" as any,
        width: animatedLargeWidth.value,
        height: animatedLargeHeight.value,
        maxWidth: "100%" as any,
        maxHeight: animatedLargeHeight.value,
        borderRadius,
        opacity,
        // Explicitly reset absolute positioning to prevent "leaking" from minimized state
        left: 0,
        top: 0,
        transform: [
          { translateX: dragOffsetX.value },
          { translateY: dragOffsetY.value + keyboardOffset.value },
        ],
      }
    } else {
      // Minimized/tab modes use absolute positioning
      const width = interpolate(
        modeProgress.value,
        [0, 0.5, 1],
        [0, animatedMinimizedWidth.value, animatedLargeWidth.value],
      )
      const height = interpolate(
        modeProgress.value,
        [0, 0.5, 1],
        [0, animatedMinimizedHeight.value, animatedLargeHeight.value],
      )

      const left = interpolate(
        modeProgress.value,
        [0, 0.5, 1],
        [-TAB_WIDTH, minimizedX, animatedLargeModeLeft.value],
      )

      const top = interpolate(
        modeProgress.value,
        [0, 0.5, 1],
        [12, effectiveTopInset + MINIMIZED_MARGIN + minimizedY, animatedLargeModeTop.value],
      )

      return {
        position: "absolute" as any,
        width,
        height,
        borderRadius,
        left,
        top,
        opacity,
        transform: [{ translateX: dragOffsetX.value }, { translateY: dragOffsetY.value }],
      }
    }
  }, [
    effectiveTopInset,
    useFlexLayout,
    isLandscape,
    snapPointsXMin,
    snapPointsXMax,
    // snapPointsYMin and snapPointsYMax removed - now using shared values which are automatically reactive
  ])

  // Animated styles for the tab (fade only, no scale)
  const tabAnimatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(modeProgress.value, [0, 0.2, 0.5], [1, 0, 0])

    return { opacity }
  })

  // Web-specific tab styles (static)
  const getWebTabStyle = useCallback(() => {
    if (Platform.OS !== "web") return {}
    return { opacity: mode === "tab" ? 1 : 0 }
  }, [mode])

  // Animated styles for controls (only visible in large mode)
  const controlsAnimatedStyle = useAnimatedStyle(() => {
    const opacity = interpolate(modeProgress.value, [0.5, 0.75, 1], [0, 0, 1])

    return { opacity }
  })

  // Web-specific control styles (static, no animation)
  const getWebControlsStyle = useCallback(() => {
    if (Platform.OS !== "web") return {}
    return { opacity: mode === "large" ? 1 : 0 }
  }, [mode])

  // Animated styles for the Rive avatar (scale in minimized and large modes)
  // Bottom offset adjustments to fine-tune alignment (proportional to container size)
  // In landscape minimized, translate down 5px; in portrait minimized, use standard offset
  const minimizedBottomOffset = isLandscape ? 10 : 5

  const riveAnimatedStyle = useAnimatedStyle(() => {
    // Interpolate scale: 1 at tab, MINIMIZED_AVATAR_SCALE at minimized, animated large scale at large
    const scale = interpolate(
      modeProgress.value,
      [0, 0.5, 1],
      [1, MINIMIZED_AVATAR_SCALE, animatedLargeAvatarScale.value],
    )
    // Interpolate translateY for bottom alignment when scaled, plus bottom offset adjustment
    // Use the actual dimensions of the Rive container to calculate offset correctly
    const currentLargeRiveHeight = animatedLargeHeight.value * 1.4
    const largeScaleOffsetValue =
      -(currentLargeRiveHeight * (animatedLargeAvatarScale.value - 1)) / 2

    // For minimized mode, we use a wider canvas (160%) to prevent side cropping
    const currentMinimizedRiveHeight = animatedMinimizedHeight.value * 1.4
    const minimizedScaleOffset = -(currentMinimizedRiveHeight * (MINIMIZED_AVATAR_SCALE - 1)) / 2

    const scaleOffset = interpolate(
      modeProgress.value,
      [0, 0.5, 1],
      [0, minimizedScaleOffset, largeScaleOffsetValue],
    )
    // Add bottom alignment fine-tuning
    const bottomOffset = interpolate(
      modeProgress.value,
      [0, 0.5, 1],
      [0, minimizedBottomOffset, animatedLargeBottomOffset.value],
    )
    const translateY = scaleOffset + bottomOffset

    return {
      transform: [{ scale }, { translateY }],
    }
  }, [minimizedBottomOffset])

  // Web-specific Rive styles (static, no animation)
  const getWebRiveStyle = useCallback(() => {
    if (Platform.OS !== "web") return {}

    if (mode === "large") {
      const scale = LARGE_AVATAR_SCALE
      const currentLargeRiveHeight = largeHeight * 1.4
      const scaleOffset = -(currentLargeRiveHeight * (scale - 1)) / 2
      const translateY = scaleOffset + LARGE_AVATAR_BOTTOM_OFFSET

      return {
        transform: [{ scale }, { translateY }],
      }
    }

    if (mode === "minimized") {
      const scale = MINIMIZED_AVATAR_SCALE
      const currentMinimizedRiveHeight = minimizedHeight * 1.4
      const scaleOffset = -(currentMinimizedRiveHeight * (scale - 1)) / 2
      const translateY = scaleOffset + minimizedBottomOffset

      return {
        transform: [{ scale }, { translateY }],
      }
    }

    return { transform: [{ scale: 1 }, { translateY: 0 }] }
  }, [mode, largeHeight, minimizedHeight, minimizedBottomOffset])

  // Animated styles for elements only visible in large mode (name plate, MSK overlay)
  const largeModeOnlyStyle = useAnimatedStyle(() => {
    const opacity = interpolate(modeProgress.value, [0.5, 0.8, 1], [0, 0, 1])

    return { opacity }
  })

  // Web-specific large mode only styles (static)
  const getWebLargeModeOnlyStyle = useCallback(() => {
    if (Platform.OS !== "web") return {}
    return { opacity: mode === "large" ? 1 : 0 }
  }, [mode])

  // Calculate MSK profile overlay size
  // Use smaller size on web (7.5% instead of 15%)
  const mskOverlaySize = mode === "large" ? Math.round(largeWidth * 0.15) : 0

  // In large flex mode, drive CC box width from the same animated value as the avatar so it resizes in sync
  const LARGE_CAPTION_MARGINS = 76 // left 12 + right 64 for control buttons
  const largeCaptionBoxAnimatedStyle = useAnimatedStyle(() => ({
    width: animatedLargeWidth.value - LARGE_CAPTION_MARGINS,
  }))

  // Derive font size from animated width so caption text scales with the box animation.
  // Cap by container's measured width so during rotation we don't use a lagging (larger)
  // animated width and compute a font that overflows the actual box.
  const captionFontSize = useDerivedValue(() => {
    const widthForFont =
      containerTargetWidthShared.value < animatedLargeWidth.value
        ? containerTargetWidthShared.value
        : animatedLargeWidth.value
    const containerWidth = widthForFont - LARGE_CAPTION_MARGINS
    const availableTextWidth = containerWidth - 24 // internal padding for large
    const textWidthSafetyFactor = 0.95
    const effectiveTextWidth = availableTextWidth * textWidthSafetyFactor
    return Math.min(18, Math.max(10, Math.floor(effectiveTextWidth / (MAX_CHARS_PER_LINE * 0.5))))
  })
  const animatedCaptionTextStyleLine1 = useAnimatedStyle(() => ({
    fontSize: captionFontSize.value,
    lineHeight: captionFontSize.value * 1.2,
  }))
  const animatedCaptionTextStyleLine2 = useAnimatedStyle(() => ({
    fontSize: captionFontSize.value,
    lineHeight: captionFontSize.value * 1.4,
  }))

  const renderCaptionContent = (
    largeCaptionAnimatedWidthStyle?: ReturnType<typeof useAnimatedStyle>,
    animatedTextStyles?: {
      line1: ReturnType<typeof useAnimatedStyle>
      line2: ReturnType<typeof useAnimatedStyle>
    },
  ) => {
    // Use "large" formatting when embedded inside the large avatar container
    const isEmbeddedLarge = mode === "large"
    // Use consistent width based on large avatar dimensions for all modes.
    // In large flex layout, use the actual measured avatar width so caption box and font size
    // match the rendered size and text isn't cut off (largeWidth can differ from laid-out width).
    // Cap at largeWidth: after rotation/resize, Avatar onLayout can still report the previous
    // width before the view has re-laid out, so we must not size the caption wider than largeWidth.
    // Prefer container's last target width when set (same-frame source of truth from onLayout).
    // When effective (avatar) width is larger than container target, the container may have run
    // with a brief smaller layout (e.g. during rotation); don't shrink the caption below avatar size.
    const usedMeasuredWidth = isEmbeddedLarge && useFlexLayout && effectiveLargeAvatarWidth != null
    const containerTarget = lastContainerTargetWidthRef.current
    const effectiveWidth = usedMeasuredWidth ? (effectiveLargeAvatarWidth as number) : largeWidth
    const widthBasis =
      containerTarget != null &&
      !(effectiveLargeAvatarWidth != null && effectiveLargeAvatarWidth > containerTarget)
        ? Math.min(effectiveWidth, containerTarget, largeWidth)
        : Math.min(effectiveWidth, largeWidth)
    const leftMargin = 12 // matches $largeCaptionContainer left position
    // In large mode: account for control buttons on the right (mute, CC, minimize)
    // Control buttons: right: 12px, button width: 44px, spacing to caption: 8px
    // Total right margin: 12 + 44 + 8 = 64px
    const rightMargin = isEmbeddedLarge ? 64 : 12
    const captionMargins = leftMargin + rightMargin
    const containerWidth = widthBasis - captionMargins
    const internalPadding = isEmbeddedLarge ? 24 : 32 // matching horizontal padding in styles
    const availableTextWidth = containerWidth - internalPadding
    // Use a conservative factor so rendered text (variable width, letter-spacing) doesn't overflow and get clipped
    const textWidthSafetyFactor = 0.9
    const effectiveTextWidth = availableTextWidth * textWidthSafetyFactor

    const fontSize = Math.min(
      18,
      Math.max(10, Math.floor(effectiveTextWidth / (MAX_CHARS_PER_LINE * charWidthRatio))),
    )

    const useAnimatedWidth = Boolean(
      isEmbeddedLarge && useFlexLayout && largeCaptionAnimatedWidthStyle,
    )
    const useAnimatedFontSize = Boolean(useAnimatedWidth && animatedTextStyles)
    const baseTextStyle = [themed($captionText), isEmbeddedLarge && { textAlign: "left" as const }]
    const captionContent =
      useAnimatedFontSize && animatedTextStyles ? (
        <>
          {!!captionText.line1 && (
            <AnimatedText
              style={
                [baseTextStyle, animatedTextStyles.line1] as React.ComponentProps<
                  typeof AnimatedText
                >["style"]
              }
              numberOfLines={1}
              ellipsizeMode="clip"
              allowFontScaling={false}
            >
              {captionText.line1}
            </AnimatedText>
          )}
          {!!captionText.line2 && (
            <AnimatedText
              style={
                [baseTextStyle, animatedTextStyles.line2] as React.ComponentProps<
                  typeof AnimatedText
                >["style"]
              }
              numberOfLines={1}
              ellipsizeMode="clip"
              allowFontScaling={false}
            >
              {captionText.line2}
            </AnimatedText>
          )}
        </>
      ) : (
        <>
          {!!captionText.line1 && (
            <Text
              style={[
                themed($captionText),
                { fontSize, lineHeight: fontSize * 1.2 },
                isEmbeddedLarge && { textAlign: "left" },
              ]}
              numberOfLines={1}
              ellipsizeMode="clip"
              allowFontScaling={false}
            >
              {captionText.line1}
            </Text>
          )}
          {!!captionText.line2 && (
            <Text
              style={[
                themed($captionText),
                { fontSize, lineHeight: fontSize * 1.4 },
                isEmbeddedLarge && { textAlign: "left" },
              ]}
              numberOfLines={1}
              ellipsizeMode="clip"
              allowFontScaling={false}
            >
              {captionText.line2}
            </Text>
          )}
        </>
      )

    if (useAnimatedWidth && largeCaptionAnimatedWidthStyle) {
      return (
        <Animated.View
          style={
            [
              themed($captionBox),
              isEmbeddedLarge ? themed($largeCaptionBox) : themed($floatingCaptionBox),
              largeCaptionAnimatedWidthStyle,
            ] as React.ComponentProps<typeof Animated.View>["style"]
          }
        >
          {captionContent}
        </Animated.View>
      )
    }

    return (
      <View
        style={[
          themed($captionBox),
          isEmbeddedLarge ? themed($largeCaptionBox) : themed($floatingCaptionBox),
          { width: containerWidth },
        ]}
      >
        {captionContent}
      </View>
    )
  }

  const floatingCaptionStyle = useAnimatedStyle(() => {
    // Visible in minimized (0.5) and tab (0) modes. Fade out as we approach large (1).
    const opacity = interpolate(modeProgress.value, [0.5, 0.8], [1, 0])
    return { opacity }
  })

  // Helper function to generate web-specific avatar styles
  const getWebAvatarStyle = useCallback(() => {
    if (Platform.OS !== "web") return {}

    if (mode === "tab") {
      return {
        position: "absolute" as const,
        width: 0,
        height: 0,
        opacity: 0,
        borderWidth: 0,
      }
    }

    if (mode === "minimized") {
      const webPos = webDrag?.webPosition

      if (!webPos) {
        return {
          position: "absolute" as const,
          width: minimizedWidth,
          height: minimizedHeight,
          borderRadius: 10,
          opacity: 0,
          // Prevent the browser from claiming touch gestures (page scroll/zoom)
          // so the avatar can be dragged with a finger on mobile web.
          touchAction: "none" as any,
        }
      }

      // webPos.x is in screen-absolute coordinates. The container is a constrained
      // column offset from the screen left by canvasLeft, so subtract it to get
      // the position relative to the container's coordinate space.
      const containerOffsetX = webLayout.canvasLeft

      return {
        position: "absolute" as const,
        width: minimizedWidth,
        height: minimizedHeight,
        left: webPos.x - containerOffsetX,
        top: webPos.y,
        borderRadius: 10,
        opacity: 1,
        touchAction: "none" as any,
      }
    }

    // Large mode
    return {
      position: "relative" as const,
      width: largeWidth,
      height: largeHeight,
      maxWidth: largeWidth,
      maxHeight: largeHeight,
      borderRadius: 20,
      opacity: 1,
    }
  }, [
    mode,
    largeWidth,
    largeHeight,
    webDrag?.webPosition,
    webLayout.canvasLeft,
    minimizedWidth,
    minimizedHeight,
  ])

  // Animated style for the root container to handle smooth inset transitions in flex mode
  const containerAnimatedStyle = useAnimatedStyle(() => {
    // When using flex layout, we no longer apply safe area padding to the container.
    // This ensures the centering logic matches our absolute positioning calculation
    // and avoids a "pop" during the transition to minimized mode.
    return {}
  })

  // dragHandlers use DOM React.PointerEvent, but Reanimated's Animated.View
  // types pointer events with RN's NativeSyntheticEvent shape. The view
  // renders to a plain element on web, so cast to bypass the type mismatch.
  const webMinimizedDragHandlers =
    Platform.OS === "web" && mode === "minimized" && webDrag ? (webDrag.dragHandlers as any) : null

  // Attach `data-touch-action="none"` (via RN-Web's `dataSet` prop) only when
  // the minimized avatar is the active touch target. This pairs with the
  // global stylesheet rule injected at module load time and forces
  // `touch-action: none !important;` on the wrapper.
  //
  // NOTE: in practice this path has proven unreliable — Reanimated's
  // `Animated.View` does not consistently forward `dataSet` to the
  // underlying DOM node, and diagnostic logs showed
  // `getComputedStyle(node).touchAction === "auto"` on the avatar wrapper
  // despite this attribute being set in the React props. We keep it as
  // belt-and-suspenders, but the authoritative fix lives in the ref-based
  // direct DOM mutation effect below (`useEffect` on `avatarWrapperWebRef`).
  const webTouchActionDataSet =
    Platform.OS === "web" && mode === "minimized"
      ? ({ touchAction: "none" } as Record<string, string>)
      : undefined

  // Web-only ref into the actual DOM node behind the Animated.View wrapper.
  // On web, Reanimated's `createAnimatedComponent(View)` ultimately renders
  // a `<div>`, and a regular React ref attached to it resolves to that DOM
  // node. We use this to set `style.touchAction = "none"` directly on the
  // touched element (and a few of its ancestors), bypassing RN-Web's
  // unreliable style/dataSet forwarding for nested wrappers.
  //
  // Why walking ancestors matters: the touch-action chain diagnostic
  // (`useWebAvatarDrag.handlePointerDown`) showed every ancestor up to 6
  // levels reported `touch-action: auto`. The browser commits a touch to a
  // native scroll/zoom gesture as soon as it sees motion, then fires
  // `pointercancel` before our React handlers can react — manifesting as
  // the avatar "snapping back" after a few pixels. Setting
  // `touch-action: none` inline on the receiving element AND a handful of
  // its ancestors blocks the browser's gesture pre-emption.
  const avatarWrapperWebRef = useRef<any>(null)

  useEffect(() => {
    if (Platform.OS !== "web") return
    if (typeof window === "undefined") return

    const node = avatarWrapperWebRef.current
    // Duck-type check: in some Reanimated/RN-Web combinations the ref may
    // briefly resolve to a non-DOM object (e.g. an internal handle) before
    // the actual element is attached.
    const el = node && typeof node === "object" && "style" in node ? (node as HTMLElement) : null
    if (!el) return

    if (mode === "minimized") {
      el.style.touchAction = "none"
      // Defensively set on a handful of ancestors. Stop at body/html to
      // avoid breaking page scroll on routes outside the avatar.
      let parent: HTMLElement | null = el.parentElement
      let depth = 0
      while (parent && depth < 5) {
        if (parent === document.body || parent === document.documentElement) break
        parent.style.touchAction = "none"
        parent = parent.parentElement
        depth += 1
      }
    } else {
      el.style.touchAction = ""
      let parent: HTMLElement | null = el.parentElement
      let depth = 0
      while (parent && depth < 5) {
        if (parent === document.body || parent === document.documentElement) break
        // Only clear if we previously set it (avoid stomping on other code's
        // inline styles). The empty string check is OK here because we know
        // we set it via this same code path.
        if (parent.style.touchAction === "none") {
          parent.style.touchAction = ""
        }
        parent = parent.parentElement
        depth += 1
      }
    }
  }, [mode])

  // Diagnostic: log when `mode` changes on web. If we see mode flipping
  // mid-drag (e.g. "minimized" -> "large" -> "minimized"), that explains why
  // pointer capture would be lost (the wrapper unmounts/remounts).
  if (DEBUG_AVATAR_GESTURE && Platform.OS === "web") {
    if (lastModeRef.current !== mode) {
      // eslint-disable-next-line no-console
      console.log("[avatar-rngh] mode changed", {
        from: lastModeRef.current,
        to: mode,
      })
      lastModeRef.current = mode
    }
  }

  if (!riveResourceName) {
    return null
  }

  return (
    <RNAnimated.View
      style={[
        useFlexLayout ? themed($flexContainer) : themed($container),
        containerAnimatedStyle,
        { opacity: avatarOpacity },
      ]}
      pointerEvents={shouldShowAvatar ? "box-none" : "none"}
      onLayout={(event) => {
        const { width, height } = event.nativeEvent.layout
        // Update dimensions based on actual rendered dimensions while maintaining aspect ratio
        // We only do this if the difference is significant to avoid micro-adjustments/flicker
        if (useFlexLayout && mode === "large") {
          // Calculate dimensions maintaining aspect ratio within available space
          const widthBasedHeight = largeWidth / LARGE_AVATAR_ASPECT_RATIO
          const heightBasedWidth = height * LARGE_AVATAR_ASPECT_RATIO

          let targetWidth: number
          let targetHeight: number

          if (widthBasedHeight <= height) {
            // Width is the limiting factor
            targetWidth = largeWidth
            targetHeight = widthBasedHeight
          } else {
            // Height is the limiting factor
            targetWidth = heightBasedWidth
            targetHeight = height
          }

          const targetTop = (height - targetHeight) / 2
          const targetLeft = (width - targetWidth) / 2

          // Keep caption width in sync with avatar target size. Avatar onLayout may not fire
          // during Reanimated-driven size changes, so set effective width from container's
          // target to avoid caption being too wide and getting clipped.
          lastContainerTargetWidthRef.current = targetWidth
          containerTargetWidthShared.value = targetWidth
          setEffectiveLargeAvatarWidth((prev) => (prev !== targetWidth ? targetWidth : prev))

          // Sync dimensions with threshold to avoid minor layout fluctuations
          if (Math.abs(animatedLargeWidth.value - targetWidth) > 5) {
            animatedLargeWidth.value = withSpring(targetWidth, rotationSpringConfig)
          }
          if (Math.abs(animatedLargeHeight.value - targetHeight) > 5) {
            animatedLargeHeight.value = withSpring(targetHeight, rotationSpringConfig)
          }
          if (Math.abs(animatedLargeModeLeft.value - targetLeft) > 5) {
            animatedLargeModeLeft.value = withSpring(targetLeft, rotationSpringConfig)
          }
          if (Math.abs(animatedLargeModeTop.value - targetTop) > 5) {
            animatedLargeModeTop.value = withSpring(targetTop, rotationSpringConfig)
          }
        }
      }}
    >
      {/* Tab Mode - Icon on the left edge; on web align with the screen edge, not the chat column */}
      <Animated.View
        style={[
          themed($tabContainer),
          { top: effectiveTopInset + 22 },
          Platform.OS === "web"
            ? {
                // Override $tabContainer's left: 12 so the tab sits 12px from the screen edge
                // rather than the constrained column's left edge.
                left: 12 - webLayout.canvasLeft,
              }
            : undefined,
          Platform.OS === "web" ? getWebTabStyle() : tabAnimatedStyle,
        ]}
        pointerEvents={mode === "tab" ? "auto" : "none"}
      >
        <TouchableOpacity style={themed($tabButton)} onPress={handleTabTap} activeOpacity={0.8}>
          <MaterialIcons name="person" size={28} color={colors.avatarControlButtonIcon} />
        </TouchableOpacity>
      </Animated.View>

      {/* Avatar Overlay (Large and Minimized modes) */}
      <GestureDetector gesture={combinedGesture}>
        <Animated.View
          ref={avatarWrapperWebRef}
          style={[
            themed($avatarWrapper),
            Platform.OS === "web" ? getWebAvatarStyle() : avatarAnimatedStyle,
            useFlexLayout && largeModeBaseStyle,
          ]}
          pointerEvents={mode === "tab" ? "none" : mode === "minimized" ? "auto" : "box-none"}
          dataSet={webTouchActionDataSet}
          onLayout={
            useFlexLayout && mode === "large"
              ? (e) => {
                  const { width } = e.nativeEvent.layout
                  const containerTarget = lastContainerTargetWidthRef.current
                  const cap = containerTarget ?? largeWidth
                  // When avatar reports larger than container target, we're growing (e.g. back to
                  // portrait after a brief small layout) - allow it so the caption grows. When
                  // avatar reports smaller, floor at container target so we don't use a stale large value.
                  const next =
                    containerTarget != null && width > containerTarget
                      ? Math.min(width, largeWidth)
                      : Math.max(Math.min(width, cap), containerTarget ?? 0)
                  setEffectiveLargeAvatarWidth((prev) => (prev !== next ? next : prev))
                }
              : undefined
          }
          {...(webMinimizedDragHandlers ?? {})}
        >
          {/* Gradient background */}
          {/* <LinearGradient
            colors={avatarGradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 0.8 }}
            style={$avatarGradient}
          /> */}
          <View style={[$avatarGradient, { backgroundColor: baseColor }]} />

          <Animated.View
            style={[
              themed($avatarRive),
              Platform.OS === "web" ? getWebRiveStyle() : riveAnimatedStyle,
              { height: "120%", bottom: 0 }, // Taller to avoid Rive clipping head in both orientations
            ]}
          >
            <AvatarRive
              resourceName={riveResourceName}
              stateMachineName="State Machine 1"
              onRiveInstanceChange={setRiveAvatarRef}
              style={$riveInner}
            />
          </Animated.View>

          {/* Name Plate (fades in for large mode) */}
          {!!avatarName && (
            <Animated.View
              style={[
                $namePlateOverlay,
                Platform.OS === "web" ? getWebLargeModeOnlyStyle() : largeModeOnlyStyle,
              ]}
              pointerEvents="none"
            >
              <Text style={themed($namePlateText)} numberOfLines={1} adjustsFontSizeToFit>
                {avatarName}
              </Text>
            </Animated.View>
          )}

          {/* MSK Profile Overlay (shown when current avatar is ESK, fades in for large mode) */}
          {!hideConversationControls && !isMsk && mskImageSource && (
            <Animated.View
              style={[
                themed($mskProfileOverlay),
                Platform.OS === "web" ? getWebLargeModeOnlyStyle() : largeModeOnlyStyle,
                {
                  width: mskOverlaySize,
                  height: mskOverlaySize,
                  borderRadius: 8,
                },
              ]}
              pointerEvents={mode === "large" ? "auto" : "none"}
            >
              <TouchableOpacity
                onPress={handleSwitchToPsk}
                activeOpacity={0.7}
                style={$mskProfileTouchable}
                accessibilityLabel="Switch to Personal Sidekick"
                accessibilityRole="button"
                disabled={mode !== "large"}
              >
                <Image source={mskImageSource} style={$mskProfileImage} contentFit="cover" />
              </TouchableOpacity>
            </Animated.View>
          )}

          {/* Control Buttons (only in large mode) */}
          <Animated.View
            style={[
              themed($controlsContainer),
              Platform.OS === "web" ? getWebControlsStyle() : controlsAnimatedStyle,
            ]}
            pointerEvents={mode === "large" ? "auto" : "none"}
          >
            {/* Mute Button — with "Unmute your sidekick" tooltip when autoplay is blocked */}
            <View style={$muteButtonRow}>
              {isAudioBlocked && mode === "large" && (
                <View style={$unmuteTooltip} pointerEvents="none">
                  <View style={$unmuteTooltipBubble}>
                    <Text style={$unmuteTooltipText} numberOfLines={1}>
                      Unmute your Sidekick
                    </Text>
                  </View>
                  <View style={$unmuteTooltipArrow} />
                </View>
              )}
              <TouchableOpacity
                style={themed($controlButton)}
                onPress={handleToggleMute}
                activeOpacity={0.7}
                accessibilityLabel={isMuted ? "Unmute" : "Mute"}
              >
                <IonIcons
                  name={isMuted ? "volume-mute" : "volume-high"}
                  size={24}
                  color={colors.avatarControlButtonIcon}
                />
              </TouchableOpacity>
            </View>

            {/* CC Toggle Button */}
            <TouchableOpacity
              style={themed($controlButton)}
              onPress={handleToggleCaptions}
              activeOpacity={0.7}
            >
              <MaterialIcons
                name={captionsEnabled ? "closed-caption" : "closed-caption-disabled"}
                size={24}
                color={colors.avatarControlButtonIcon}
              />
            </TouchableOpacity>

            {/* Minimize Button */}
            <TouchableOpacity
              style={themed($controlButton)}
              onPress={handleMinimize}
              activeOpacity={0.7}
            >
              <MaterialIcons
                name="picture-in-picture-alt"
                size={24}
                color={colors.avatarControlButtonIcon}
              />
            </TouchableOpacity>
          </Animated.View>

          {/* CC Box for Large Mode - Rendered inside container to match its rounding and bounds */}
          {shouldShowCaptions && shouldShowCaptionsForMode && (
            <Animated.View
              style={[
                $largeCaptionContainer,
                Platform.OS === "web" ? getWebLargeModeOnlyStyle() : largeModeOnlyStyle,
              ]}
              pointerEvents="none"
            >
              {renderCaptionContent(largeCaptionBoxAnimatedStyle, {
                line1: animatedCaptionTextStyleLine1,
                line2: animatedCaptionTextStyleLine2,
              })}
            </Animated.View>
          )}
        </Animated.View>
      </GestureDetector>

      {/* Floating CC Box for Minimized/Tab Mode - Uses flex layout for consistent positioning */}
      {shouldShowCaptions && shouldShowCaptionsForMode && !useFlexLayout && (
        <Animated.View
          style={[$floatingCaptionContainerFlex, floatingCaptionStyle]}
          pointerEvents="none"
        >
          {renderCaptionContent()}
        </Animated.View>
      )}
    </RNAnimated.View>
  )
}

/**
 * Hook to manage avatar visibility based on speech/processing states.
 * Returns the consolidated state from SidekickInteractionContext.
 */
export function useAvatarVisibility() {
  const { shouldShowAvatar } = useSidekickInteraction()

  return {
    shouldShowAvatar,
  }
}

// AvatarOverlay zIndex strategy
// ─────────────────────────────
// Outer stacking against other app UI (chat content, title bar, etc.) is owned
// by SidekickView's `avatarRegion` wrapper (zIndex 10000). Inside this
// component the only stacking that matters is the relative order among our own
// siblings/children. We deliberately keep these values in the single digits so
// they DO NOT translate to high native `elevation` on Android — high elevation
// (≫ Material's 24dp norm) makes views compete with `Modal` windows for touch
// dispatch, which manifested as the SessionTakeoverDialog buttons being
// unresponsive (taps in the dialog area landed on the avatar's elevated
// subtree instead of the Modal). See react-native-modal#584 and
// facebook/react-native#50589 for background.
//
// Relative ordering within the AvatarOverlay root:
//   floating caption (3) > avatar wrapper (2) > tab container (1)
// Inside the avatar wrapper:
//   large caption (3) > nameplate / MSK / controls (2) > Rive / gradient (0)
const $container: ThemedStyle<ViewStyle> = () => {
  if (Platform.OS === "web") {
    // On web, constrain to centered iPad-style canvas
    // Use alignSelf and marginHorizontal for centering instead of transform
    return {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: "100%",
      width: "100%",
      maxWidth: 720,
      marginHorizontal: "auto",
      flexDirection: "column" as const,
      justifyContent: "flex-start" as const,
    }
  }

  return {
    ...StyleSheet.absoluteFillObject,
    flexDirection: "column",
    justifyContent: "center",
  }
}

const $flexContainer: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
  width: "100%",
  justifyContent: "center", // Center avatar vertically
  alignItems: "center",
})

const $avatarWrapper: ThemedStyle<ViewStyle> = ({ colors }) => ({
  // position: "absolute" is now conditional and applied via avatarAnimatedStyle
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
  borderColor: colors.avatarBorder,
  borderWidth: 1,
  zIndex: 2,
})

const $avatarRive: ThemedStyle<ViewStyle> = () => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  width: "100%",
  height: "100%",
  overflow: "visible", // Allow head to stick out during transformations
})

const $riveInner: ViewStyle = {
  width: "100%",
  height: "100%",
}

const $avatarGradient: ViewStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
}

const $namePlateOverlay: ViewStyle = {
  position: "absolute",
  left: 16,
  top: 8,
  zIndex: 2,
}

const $namePlateText: ThemedStyle<TextStyle> = ({ colors, typography }) => ({
  color: colors.text,
  fontSize: 16,
  fontFamily: typography.fonts.figtree.medium,
  fontWeight: "400",
})

const $mskProfileOverlay: ThemedStyle<ViewStyle> = ({ colors }) => ({
  position: "absolute",
  right: 8,
  top: 8,
  borderWidth: 0,
  overflow: "hidden",
  backgroundColor: colors.mskProfileBackground,
  zIndex: 2,
})

const $mskProfileTouchable: ViewStyle = {
  width: "100%",
  height: "100%",
}

const $mskProfileImage: ImageStyle = {
  width: "100%",
  height: "100%",
}

const $controlsContainer: ViewStyle = {
  position: "absolute",
  bottom: 8,
  right: 12,
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-end",
  gap: 6,
  zIndex: 2,
}

const $controlButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: 44,
  height: 44,
  borderRadius: 22,
  backgroundColor: colors.avatarControlButtonBackground,
  justifyContent: "center",
  alignItems: "center",
})

const $muteButtonRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
}

const TOOLTIP_BG = "rgba(0, 0, 0, 0.82)"

const $unmuteTooltip: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  marginRight: 8,
}

const $unmuteTooltipBubble: ViewStyle = {
  backgroundColor: TOOLTIP_BG,
  paddingHorizontal: 10,
  paddingVertical: 6,
  borderRadius: 8,
}

const $unmuteTooltipText: TextStyle = {
  color: "#FFFFFF",
  fontSize: 13,
  fontWeight: "600",
}

const $unmuteTooltipArrow: ViewStyle = {
  width: 0,
  height: 0,
  borderTopWidth: 6,
  borderBottomWidth: 6,
  borderLeftWidth: 8,
  borderTopColor: "transparent",
  borderBottomColor: "transparent",
  borderLeftColor: TOOLTIP_BG,
}

const $tabContainer: ViewStyle = {
  position: "absolute",
  left: 12,
  zIndex: 1,
}

const $tabButton: ThemedStyle<ViewStyle> = ({ colors }) => ({
  width: TAB_SIZE,
  height: TAB_SIZE,
  borderRadius: TAB_SIZE / 2,
  backgroundColor: colors.avatarControlButtonBackground,
  justifyContent: "center",
  alignItems: "center",
})

const $floatingCaptionContainerFlex: ViewStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  alignItems: "center",
  paddingHorizontal: 16,
  paddingBottom: 12, // Match the 12px spacing from large mode
  zIndex: 3,
}

const $largeCaptionContainer: ViewStyle = {
  position: "absolute",
  bottom: 8,
  left: 8,
  zIndex: 3,
}

const $captionBox: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.avatarCaptionBackground,
  paddingHorizontal: 16,
  paddingVertical: 6,
  borderRadius: 8,
  maxWidth: "90%",
  shadowColor: "#000",
  shadowOffset: {
    width: 0,
    height: 2,
  },
  shadowOpacity: 0.3,
  shadowRadius: 4,
  elevation: 5,
})

const $largeCaptionBox: ThemedStyle<ViewStyle> = ({ colors }) => ({
  maxWidth: "100%",
  backgroundColor: colors.avatarCaptionBackground,
  borderBottomLeftRadius: 20, // Match container rounding (20 container radius)
  paddingHorizontal: 12,
  paddingVertical: 6,
  alignItems: "flex-start",
})

const $floatingCaptionBox: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.avatarCaptionBackground,
  borderRadius: 8,
  paddingVertical: 6,
  paddingHorizontal: 16,
})

const $captionText: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.text,
  fontSize: 18,
  fontWeight: "600",
  textAlign: "center",
  letterSpacing: 0.25,
  lineHeight: 22,
  textShadowColor: colors.captionTextShadow,
  textShadowOffset: { width: 1, height: 1 },
  textShadowRadius: 3,
})
